import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import { Formik, Form } from "formik";
import M from "materialize-css";
import { FieldValue, serverTimestamp, arrayUnion } from "firebase/firestore";

import { db } from "../lib/firebaseCompat";
import Row from "../components/shared/Row";
import Information from "../components/EstimateForm/Information";
import Assets from "../components/EstimateForm/Assets";
import MetaData from "../components/EstimateForm/MetaData";
import Notes from "../components/EstimateForm/Notes";

import { useMaterialInputFields } from "../hooks/useMaterialInputFields";
import { useOrganization } from "../hooks/useOrganization";
import { useAuth } from "../hooks/usehooks.com/use-auth";

const parseTerms = (name) => {
  const token = " ";
  const terms = name
    .replace(/\W/g, token)
    .replace(/\s+/g, token)
    .toLowerCase()
    .split(token)
    .map((t) => t.trim())
    .reduce((acc, t) => (t ? [...acc, t] : acc), []);

  return [...terms, name.toLowerCase()];
};

export default function EstimateForm({
  initialEstimate,
  docId,
  readOnly = false,
}) {
  useMaterialInputFields();

  const navigate = useNavigate();
  const { user } = useAuth();
  const organization = useOrganization();
  const orgId = organization.current?.id;

  const handleSubmit = async (values) => {
    const ref = db
      .collection("organizations")
      .doc(orgId)
      .collection("estimates")
      .doc(docId);

    const { status, searchTerms = [], businessName } = values;
    const businessSearchTerms = parseTerms(businessName);
    const indexSearchTerms = [...searchTerms, ...businessSearchTerms];
    const data = {
      ...values,
      businessSearchTerms,
      indexSearchTerms,
      updatedAt: serverTimestamp(),
    };
    const saveDraftMsg = "Estimate saved successfully";
    const savePublishMsg = "Estimate saved and published successfully";
    const msg = status === "draft" ? saveDraftMsg : savePublishMsg;

    try {
      await ref.set(data);
      M.toast({ html: msg });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      M.toast({ html: "There was an error saving this estimate" });
    }
  };

  const handleCopyLegacy = async () => {
    const orgRef = db
      .collection("organizations")
      .doc(orgId);

    await orgRef.update({
      migratedIds: arrayUnion(initialEstimate.id)
    });

    const estimateRef = orgRef.collection("estimates").doc();
    
    const legacyData = {
      ...initialEstimate,
      legacyId: initialEstimate.id,
      status: "published",
      createdBy: user.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const { id, ...result} = legacyData;

    try {
      await estimateRef.set(result);
      M.toast({ html: "Estimate copied successfully" });
      setTimeout(() => navigate(`/my`), 2000);
    } catch (err) {
      console.error(err);
      M.toast({ html: "There was an error saving this estimate" });
    }
  }

  const [ isEstimateCopied, setIsEstimateCopied ] = useState(true);
  useEffect(() => {
    (async () => {
      const ref = await db
        .collection("organizations")
        .doc(orgId)
        .get();
      const data = ref.data();
      const estimateState = data.migratedIds && data.migratedIds.includes(initialEstimate.id);
      setIsEstimateCopied(estimateState);
    })()
  }, []);

  return (
    <Formik initialValues={initialEstimate} onSubmit={handleSubmit}>
      {({ submitForm, setFieldValue, values: { status } }) => (
        <Form>
          <Information estimate={initialEstimate} />
          <Assets />
          <MetaData />
          <Notes />
          {
            readOnly && !isEstimateCopied &&
            (
              <div className="col right">
                <button
                  type="button"
                  className="btn right green save-button"
                  onClick={() => handleCopyLegacy()}
                >
                  Migrate to My Estimates
                </button>
              </div>
            )
          }

          {
            !readOnly && (
              <Row>
                <div className="col right">
                  <button type="submit" className="btn right save-button">
                    Save
                  </button>
                  {status === "draft" && (
                    <button
                      type="submit"
                      className="btn right green save-button"
                      onClick={(evt) => {
                        evt.preventDefault();
                        setFieldValue("status", "published");
                        submitForm();
                      }}
                    >
                      Publish
                    </button>
                  )}
                </div>
              </Row>
            )
          }
        </Form>
      )}
    </Formik>
  );
}

const AssetShape = PropTypes.shape({
  name: PropTypes.string,
  cost: PropTypes.number,
});

EstimateForm.propTypes = {
  docId: PropTypes.string.isRequired,
  initialEstimate: PropTypes.shape({
    name: PropTypes.string,
    naics: PropTypes.string,
    notes: PropTypes.string,
    assets: PropTypes.array,
    newAsset: AssetShape,
  }),
};
